import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

const ProductCarousel = ({ webImages, mobImages }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const nextSlideButtonRef = useRef(null);

  // Use useCallback for the resize handler to prevent unnecessary re-renders
  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);
  
  // Memoize the images array and showArrows calculations to prevent recalculation on every render
  const images = useMemo(() => isMobile ? mobImages : webImages, [isMobile, mobImages, webImages]);
  const showArrows = useMemo(() => images.length > 1, [images]);

  // Memoize the carousel indicators to prevent unnecessary re-renders
  const carouselIndicators = useMemo(() => {
    if (images.length <= 1) return null;
    
    return (
      <div className="carousel-indicators">
        {images.map((_, index) => (
          <button
            key={index}
            type="button"
            data-bs-target="#carouselExampleIndicators"
            data-bs-slide-to={index}
            className={index === 0 ? "active" : ""}
            aria-label={`Slide ${index + 1}`}
          ></button>
        ))}
      </div>
    );
  }, [images]);

  // Memoize the carousel items to prevent unnecessary re-renders
  const carouselItems = useMemo(() => {
    return images.map((image, index) => (
      <div key={index} className={`carousel-item ${index === 0 ? "active" : ""}`}>
        <LazyLoadImage
          src={image.imgSrc}
          width="100%"
          height="100%"
          className="d-block img-fluid"
          alt={image.altText}
          effect="blur"
          fetchpriority="high"
          loading="eager"
        />
      </div>
    ));
  }, [images]);

  // Memoize the navigation arrows to prevent unnecessary re-renders
  const navigationArrows = useMemo(() => {
    if (!showArrows) return null;
    
    return (
      <>
        <button className="carousel-control-prev" type="button" data-bs-target="#carouselExampleIndicators" data-bs-slide="prev">
          <span className="carousel-control-prev-icon" aria-hidden="true"></span>
          <span className="visually-hidden">Previous</span>
        </button>

        <button
          className="carousel-control-next"
          type="button"
          id="next-slide"
          data-bs-target="#carouselExampleIndicators"
          data-bs-slide="next"
          ref={nextSlideButtonRef}
        >
          <span className="carousel-control-next-icon" aria-hidden="true"></span>
          <span className="visually-hidden">Next</span>
        </button>
      </>
    );
  }, [showArrows, nextSlideButtonRef]);

  return (
    <div id="carouselExampleIndicators" className="carousel slide" data-bs-ride="carousel" data-bs-interval="10000">
      {carouselIndicators}
      <div className="carousel-inner">
        {carouselItems}
      </div>
      {navigationArrows}
    </div>
  );
};

export default React.memo(ProductCarousel);
